














































import {
  defineComponent,
  computed,
  PropType,
  ref,
  watch,
  vue3Model
} from '~/utils/nuxt3-migration'
import { useI18n } from '~/compositions/i18n'
import { GeolocationService } from '~/services/navigator/GeolocationService'
import { useDeps } from '~/compositions/dependency-container'
import { GoogleAutocompleteInputMode } from '~/models/google'
import { InputSize } from '~/models/app/input'
import { useSnackbar } from '~/compositions/snackbar'
import { ciLocationPin } from '~/icons/source/solid/location-pin'
import { ciTimes } from '~/icons/source/solid/times'
import CInputAutocomplete from '~/components/shared/configurable/form/input/CInputAutocomplete.vue'
import { AutocompleteSuggestion } from '~/models/autocomplete'
import { GoogleLocationAutocomplete } from '~/models/location/autocomplete/types'
import LocationService from '~/services/location/LocationService'
import { debounce } from '~/utils/function'
import { useId } from '~/compositions/id'

export default defineComponent({
  components: { CInputAutocomplete },
  model: vue3Model,
  props: {
    modelValue: {
      type: String,
      default: null
    },
    gps: {
      type: Boolean,
      default: false
    },
    size: {
      type: String as PropType<InputSize>,
      default: 'md'
    },
    placeholder: {
      type: String,
      default: ''
    },
    required: {
      type: Boolean,
      default: false
    },
    countryCodes: {
      type: Array as PropType<string[]>,
      default() {
        return ['gr', 'cy', 'bg', 'al', 'tr', 'mk']
      }
    },
    isValid: {
      type: Boolean,
      default: true
    },
    mode: {
      type: String as PropType<GoogleAutocompleteInputMode>,
      default: GoogleAutocompleteInputMode.ADDRESS
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  setup(props, { emit }) {
    const { t } = useI18n()
    const snackbar = useSnackbar()
    const { uuid } = useId()

    const [geolocationService, locationService] = useDeps(
      GeolocationService,
      LocationService
    )

    const loading = ref<boolean>(false)
    const internalValue = ref(props.modelValue)
    const suggestions = ref<AutocompleteSuggestion[]>([])
    const sessionToken = ref(uuid())

    const isPostcodeMode = computed(
      () => props.mode === GoogleAutocompleteInputMode.POSTCODE
    )

    watch(
      () => props.modelValue,
      newValue => {
        internalValue.value = newValue
      }
    )

    function onInput(input: string) {
      internalValue.value = input
      emit('update:modelValue', internalValue.value)
    }

    async function onSuggestionSelect(suggestion: AutocompleteSuggestion) {
      const { place } = await locationService.geocodeGooglePlace(suggestion.id)

      setInternalValueBasedOnMode(place)
      emit('change', place)
      sessionToken.value = uuid()
    }

    const onSuggestionsRequest = debounce(async () => {
      try {
        loading.value = true
        const { results } = await locationService.getLocationAutocompletePlaces(
          internalValue.value,
          props.countryCodes,
          sessionToken.value
        )

        suggestions.value = results.map(p => ({
          id: p.placeId,
          name: p.mainText,
          subText: p.secondaryText
        }))
      } catch (err) {
      } finally {
        loading.value = false
      }
    })

    function clear() {
      if (props.disabled) {
        return
      }

      internalValue.value = null
      suggestions.value = []
      sessionToken.value = uuid()

      emit('change', {
        city: null,
        address: null,
        postcode: null,
        geolocation: null
      })
    }

    async function getCurrentGpsLocation() {
      if (props.disabled) {
        return
      }

      try {
        const gpsGeolocation = await geolocationService.getCurrentGpsLocation()

        if (!gpsGeolocation) {
          return
        }

        const { results } = await locationService.geocodeGeolocation(
          gpsGeolocation
        )

        const result = results[0]
        const toEmit = { ...result, geolocation: gpsGeolocation }
        setInternalValueBasedOnMode(toEmit)
        emit('change', toEmit)
      } catch (err) {
        snackbar.error(t('failed to get gps location'))
      }
    }

    function setInternalValueBasedOnMode(values: GoogleLocationAutocomplete) {
      const { postcode, city, country, address } = values
      switch (props.mode) {
        case GoogleAutocompleteInputMode.POSTCODE: {
          internalValue.value = postcode
          break
        }
        case GoogleAutocompleteInputMode.CITY: {
          internalValue.value = city
          break
        }
        case GoogleAutocompleteInputMode.COUNTRY: {
          internalValue.value = country
          break
        }
        case GoogleAutocompleteInputMode.ADDRESS:
        default: {
          internalValue.value = address
          break
        }
      }
    }

    return {
      ciLocationPin,
      ciTimes,
      loading,
      isPostcodeMode,
      internalValue,
      getCurrentGpsLocation,
      clear,
      onInput,
      suggestions,
      onSuggestionSelect,
      onSuggestionsRequest
    }
  }
})
